import { mapValues, sortBy } from "lodash-es";

import { captureMessage } from "@/error/sentry";
import type { StickerType } from "@/model/StickerType";
import type { Color, IdMap } from "@/model/baseTypes";
import { statusClasses } from "@/model/baseTypes";
import type { BoardIteration, Kanban } from "@/model/board";
import type { LinkedSticky, Reaction, Reactions, SearchResult } from "@/model/card";
import { reactions, roundPoints } from "@/model/card";
import type { Category, FlexType } from "@/model/flexboard";
import type { Link } from "@/model/link";
import type { Art, Iteration, Session, Team } from "@/model/session";
import type { StatusClassDistribution } from "@/model/status";
import type { AlmStickyType, Priority, StickyType } from "@/model/stickyType";
import type { TimerEvent } from "@/model/timer";
import type { PersonalUser } from "@/model/user";
import { almOnlyUser } from "@/model/user";
import { useAlmItemTypeStore } from "@/store/almItemType";
import type { Settings } from "@/store/serverSettings";
import { useStickyTypeStore } from "@/store/stickyType";
import { colorFromNumber } from "@/utils/color";
import { plusDays, startOfUtcDay } from "@/utils/date";
import { fromIsoDateString, fromUtc } from "@/utils/dateFormat";
import { entriesIn, mapObject } from "@/utils/record";
import { sessionStartDate } from "@/utils/session";
import { parseStickerString } from "@/utils/sticker";
import { hash } from "@/utils/string";

import { loadReactiveUser } from "../http/user";
import type {
  ServerAlmStickyTypeData,
  ServerArt,
  ServerBoardIteration,
  ServerCardLinkedSticky,
  ServerCategory,
  ServerCompanySettings,
  ServerEvent,
  ServerFlexType,
  ServerIteration,
  ServerIterationUpdate,
  ServerKanbanConfigMap,
  ServerLink,
  ServerSearchResult,
  ServerSession,
  ServerSettings,
  ServerStatusDistribution,
  ServerStickerType,
  ServerStickyType,
  ServerTeam,
} from "../serverModel";

export function mapSessions(sessions: ServerSession[]): Session[] {
  return sessions
    .map((session) => ({
      id: session.session_id,
      name: session.name,
      creationDate: startOfUtcDay(fromUtc(session.creation_date)),
      planningStart: session.planning_dates?.start
        ? fromIsoDateString(session.planning_dates.start)
        : null,
      planningEnd: session.planning_dates?.end
        ? fromIsoDateString(session.planning_dates.end)
        : null,
      archived: session.archived,
      almStatus: null,
      iterations: mapIterations(session.iterations),
    }))
    .sort((a, b) => sessionStartDate(b).getTime() - sessionStartDate(a).getTime());
}

export function mapStickerTypes(types: ServerStickerType[]): StickerType[] {
  return types.map((type) => ({
    icon: type.icon,
    color: type.color,
    label: type.label,
  }));
}

export function mapLink(link: ServerLink): Link {
  return { id: link.id, from: link.from_sticky_id, to: link.to_sticky_id };
}

export function mapLinks(links: ServerLink[]): Link[] {
  return links.map(mapLink);
}

export const emptyBoardIteration = (): BoardIteration => ({ items: 0, load: 0, velocity: 0 });

export function mapBoardIterations(bis: ServerBoardIteration[]): BoardIteration[] {
  return sortBy(bis, (iter) => iter.board_iteration_id).map((bi) =>
    mapIterationUpdate(bi, emptyBoardIteration()),
  );
}

export function mapIterationUpdate(
  update: ServerIterationUpdate,
  existing: BoardIteration,
): BoardIteration {
  const statusDistribution = update.status_distribution
    ? mapStatusDistribution(update.status_distribution)
    : undefined;

  // Handle velocity being sent as a string
  // "undefined" or "null" should be treated as undefined
  const velocity = parseFloat("" + update.velocity);
  const updateVelocity = Number.isFinite(velocity) ? velocity : undefined;

  return {
    ...existing,
    ...(updateVelocity !== undefined && { velocity: updateVelocity }),
    ...(statusDistribution && { ...totals(statusDistribution), statusDistribution }),
  };
}

function totals(statusDistribution: StatusClassDistribution) {
  // statusDistribution.values() directly is not working on bloody safari 17
  return [...statusDistribution.values()].reduce(
    (sum, value) => ({ items: sum.items + value.value, load: sum.load + value.points }),
    { items: 0, load: 0 },
  );
}

export function mapStickyType(stickyType: ServerStickyType): StickyType {
  const priorities = stickyType.step_estimate
    ? prioritiesByValue(stickyType.step_estimate_values)
    : undefined;
  const color = checkColor(stickyType.id, stickyType.color);
  return {
    id: stickyType.id,
    name: stickyType.name,
    almType: stickyType.alm_type,
    color,
    altColor: stickyType.alt_color || color,
    priorities,
    functionality: stickyType.functionality,
    origin: stickyType.origin_board_type,
    flexOrigin: stickyType.origin_flexboard_type || null,
    usable: stickyType.available_board_types,
    flexUsable: stickyType.available_flexboard_types || [],
  };

  function prioritiesByValue(stepEstimates?: Priority[]) {
    if (stepEstimates) {
      return Object.fromEntries(
        stepEstimates.map((stepEstimate) => [stepEstimate.value, stepEstimate]),
      );
    }
  }

  function checkColor(id: string, c: Color | undefined | null): Color {
    if (c) {
      return c;
    }
    captureMessage("Sticky type has no color", { stickyType: { id } });
    return [0.5, 0.5, 0.5, 1];
  }
}

export function mapAlmStickyTypes(almStickyTypes: ServerAlmStickyTypeData[]): IdMap<AlmStickyType> {
  return Object.fromEntries(
    almStickyTypes.map((almType) => [almType.id, { id: almType.id, isMapped: almType.is_mapped }]),
  );
}

/** @internal exported for tests */
export function mapIterations(iters: ServerIteration[]): Iteration[] {
  return iters.map((iter) => ({
    id: iter.id,
    name: iter.name,
    start: startOfUtcDay(fromUtc(iter.start)),
    end: plusDays(startOfUtcDay(fromUtc(iter.end)), 1),
  }));
}

export function mapFlexTypes(types: ServerFlexType[]): FlexType[] {
  return types.map((type) => ({ id: type.id, name: type.name, background: type.background }));
}

export function mapSearchResults(results: ServerSearchResult[]): SearchResult[] {
  return results.map((result) => ({
    boardId: result.board_id,
    id: result.id,
    text: result.text ?? "",
    typeId: result.type_id,
    teamId: result.team_id?.toString(),
    artId: result.art_id,
    flagType: parseStickerString(result.flag_type),
    almId: result.alm_issue_id,
    almIssueUrl: result.alm_issue_url,
    iterationId: result.iteration_number,
    groupId: result.sticky_group,
    status: useAlmItemTypeStore().calcStatus(
      result.status,
      useStickyTypeStore().findStickyType(result.type_id),
      result.team_id?.toString(),
      result.art_id,
      result.alm_source ?? null,
      result.status_class,
    ),
    dependTeamId: result.depend_team_id?.toString(),
    precondTeamId: result.precond_team_id?.toString(),
    points: roundPoints(result.story_points),
    priority: result.WSJF_value ?? undefined,
    objectives: result.objectives,
  }));
}

export function mapEvents(events: ServerEvent[]): TimerEvent[] {
  return events.flatMap((event) =>
    event.type === "timer"
      ? [
          {
            id: event.id,
            boardId: event.board_id,
            artId: event.art_id,
            type: "timer",
            createdById: event.created_by,
            createdAt: event.created_at,
            updatedById: event.updated_by,
            updatedAt: event.updated_at,
            data: {
              name: event.data.name,
              state: event.data.state,
              duration: event.data.duration,
              end: event.data.end,
            },
          },
        ]
      : [],
  );
}

export function mapReactions(reactionUsers: Partial<Record<Reaction, string[]>>): Reactions {
  const res = {} as Reactions;
  for (const reaction of reactions) {
    res[reaction] = (reactionUsers[reaction] || []).map((id) =>
      loadReactiveUser({ id, caller: "reaction" }),
    );
  }
  return res;
}

export function mapCategories(categories: ServerCategory[]): Category[] {
  return categories.map((cat) => ({ id: cat._id, name: cat.name, boardIds: cat.board_ids }));
}

interface ArtsAndTeams {
  arts: Art[];
  teams: Team[];
  externals: Team[];
}

export function mapArtsAndTeams(arts: ServerArt[]): ArtsAndTeams {
  return {
    arts: arts.map((art) => ({ id: art.id ? "" + art.id : "", name: art.name })),
    teams: arts.flatMap((art) =>
      art.teams.filter((team) => !team.external).map((team) => mapTeam(art, team, false)),
    ),
    externals: arts.flatMap((art) =>
      art.teams.filter((team) => team.external).map((team) => mapTeam(art, team, true)),
    ),
  };
}

function mapTeam(art: ServerArt, team: ServerTeam, external: boolean): Team {
  return { id: "" + team.id, name: team.name, artId: art.id ? "" + art.id : undefined, external };
}

type SettingValue = boolean | number | null;

const settingsMappings = (() => {
  return {
    isBacklogAlmStickyDeletable: [not, "backlog_forbid_JIRA_sticky_delete"],
    isTeamAlmStickyDeletable: [not, "team_forbid_sticky_delete"],
    isPriorityEditable: [not, "backlog_forbid_WSJF_edit"],
    confirmDelete: [orFalse, "confirm_sticky_delete"],
    moveBetweenTeams: [orFalse, "move_activated"],
    mirrorAssignedBacklogStickiesToTeamBoard: [
      orFalse,
      "backlog_mirror_assigned_stickies_to_team_board",
    ],
    iterationLoadWarn: [orNull, "iteration_load_warn"],
    iterationLoadCritical: [orNull, "iteration_load_critical"],
    showDevLinks: [orFalse, "show_dev_links"],
  } as const;

  function not(value?: SettingValue) {
    return !value;
  }

  function orFalse(value?: SettingValue) {
    return value || false;
  }

  function orNull(value?: SettingValue) {
    return value || null;
  }
})();

export function mapSettings(settings: ServerSettings): Settings {
  const s = mapValues(settingsMappings, ([mapper, prop]) => mapper(settings[prop])) as Settings;
  s.isBacklogAlmStickyDeletable &&= !settings.disable_delete;
  s.isTeamAlmStickyDeletable &&= !settings.disable_delete;
  return s;
}

export function mapSetting(prop: keyof ServerSettings, value: SettingValue): Partial<Settings> {
  const entry = Object.entries(settingsMappings).find(([_k, [_m, p]]) => p === prop);
  if (!entry) {
    return {};
  }
  const [key, [mapper, _]] = entry;
  return { [key]: mapper(value) };
}

export function mapCompanySettings(data: ServerCompanySettings = {}) {
  return { autoLogoutAfter: data.auto_logout_after || 0 };
}

export function mapUserId(userId?: string | null, almId?: string | null): PersonalUser | null {
  return userId
    ? loadReactiveUser(
        { id: userId, caller: "mapUserId" },
        { almMapping: almId ? "complete" : "pip-only" },
      )
    : almId
      ? almOnlyUser(almId, colorFromNumber(hash(almId)))
      : null;
}

export function mapLinkedSticky(linkedSticky: ServerCardLinkedSticky): LinkedSticky {
  return {
    stickyId: linkedSticky.sticky_id,
    linkId: linkedSticky.link_id,
    direction: linkedSticky.direction,
  };
}

export function mapStatusDistribution(
  distribution: ServerStatusDistribution,
): StatusClassDistribution {
  return new Map(
    entriesIn(distribution)
      // Sort classes from undefined -> done
      .sort(([s1], [s2]) => statusClasses.indexOf(s1) - statusClasses.indexOf(s2))
      .map(([status, data]) => [status, { points: data?.points ?? 0, value: data?.value ?? 0 }]),
  );
}

export function mapKanbanConfigs(kanban: ServerKanbanConfigMap): IdMap<Kanban[]> {
  return mapObject(kanban, (teamId, configs) => [
    "" + teamId,
    configs.map((config) => ({
      name: config.name,
      columns: config.columns.map((column) => ({ name: column.name, statuses: column.statuses })),
    })),
  ]);
}
